import React, { useState, useEffect } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import { Loader2, Moon } from "lucide-react";
import { Toaster, toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PortalLanding } from "@/components/PortalLanding";
import { PortalTransition } from "@/components/PortalTransition";
import { ReadingForm } from "@/components/ReadingForm";
import { CardDraw } from "@/components/CardDraw";
import { ResultView } from "@/components/ResultView";
import { POSITIONS } from "@/data/deck";
import { T } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Loading = ({ t }) => {
  const [pct, setPct] = useState(4);
  useEffect(() => {
    const id = setInterval(() => {
      setPct((p) => (p < 92 ? p + Math.max(0.4, (92 - p) * 0.012) : p));
    }, 500);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6" data-testid="loading-screen">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        className="mb-8"
      >
        <Moon className="w-14 h-14 text-primary" />
      </motion.div>
      <h2 className="font-serif-display text-3xl sm:text-4xl rose-gold-text glow-rose mb-4 max-w-md">{t.loading_title}</h2>
      <p className="text-muted-foreground max-w-sm mb-6">{t.loading_sub}</p>
      <Loader2 className="w-6 h-6 text-primary animate-spin" />
      <div className="w-64 h-1.5 bg-secondary rounded-full mt-8 overflow-hidden" data-testid="loading-progress">
        <motion.div
          className="h-full bg-gradient-to-r from-[#f7c873] to-[#ff8fa3]"
          animate={{ width: `${pct}%` }}
          transition={{ ease: "linear", duration: 0.5 }}
        />
      </div>
    </div>
  );
};

function App() {
  const [lang, setLang] = useState("de");
  const [step, setStep] = useState("landing");
  const [form, setForm] = useState(null);
  const [drawn, setDrawn] = useState([]);
  const [reading, setReading] = useState(null);
  const t = T[lang];

  const reset = () => {
    setStep("landing");
    setForm(null);
    setDrawn([]);
    setReading(null);
  };

  const handleReveal = async (cards) => {
    setDrawn(cards);
    setStep("loading");
    const payload = {
      language: lang,
      name: form.name,
      partner_name: form.partner_name,
      birth_date: form.birth_date,
      partner_birth_date: form.partner_birth_date,
      question: form.question,
      cards: cards.map((c, i) => ({
        id: c.id,
        name_de: c.name_de,
        name_en: c.name_en,
        keyword: lang === "de" ? c.keyword_de : c.keyword_en,
        position_de: POSITIONS[i].de,
        position_en: POSITIONS[i].en,
      })),
    };
    try {
      const res = await fetch(`${API}/reading`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("request_failed");
      const { job_id } = await res.json();
      if (!job_id) throw new Error("no_job_id");

      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      let attempts = 0;
      while (attempts < 160) {
        attempts += 1;
        await sleep(2500);
        let job;
        try {
          const pr = await fetch(`${API}/reading/${job_id}`);
          if (!pr.ok) continue;
          job = await pr.json();
        } catch {
          continue;
        }
        if (job.status === "done" && job.reading) {
          setReading(job.reading);
          setStep("result");
          window.scrollTo({ top: 0 });
          return;
        }
        if (job.status === "error") throw new Error(job.message || "generation_error");
      }
      throw new Error("timeout");
    } catch (e) {
      console.error(e);
      toast.error(t.err);
      setStep("draw");
    }
  };

  const handleDownload = () => {
    if (!reading) return;
    const nameOf = (c) => (lang === "de" ? c.name_de : c.name_en);
    const esc = (s) => String(s || "").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const cardsHtml = (reading.cards || [])
      .map((rc, i) => {
        const card = drawn[i];
        return `<div class="card"><h3>${esc(rc.name || (card && nameOf(card)))} — <span>${esc(rc.position)}</span></h3>
          ${card ? `<img src="${card.img}" />` : ""}
          <p><b>${esc(t.classic)}:</b> ${esc(rc.classic)}</p>
          <p><b>${esc(t.soulmate)}:</b> ${esc(rc.soulmate)}</p>
          <p><b>${esc(t.partner_feelings)}:</b> ${esc(rc.partner_feelings)}</p>
          <p><b>${esc(t.development)}:</b> ${esc(rc.development)}</p>
          <p><b>${esc(t.blockage)}:</b> ${esc(rc.blockage)}</p></div>`;
      })
      .join("");
    const ch = reading.chakras || {};
    const chakraHtml = [["heart", t.chakra_heart], ["solar_plexus", t.chakra_solar], ["third_eye", t.chakra_third], ["root", t.chakra_root]]
      .filter(([k]) => ch[k])
      .map(([k, label]) => `<p><b>${esc(label)}:</b> ${esc(ch[k].blockage)} — ${esc(ch[k].influence)} — ${esc(ch[k].healing)}</p>`)
      .join("");
    const lv = reading.love || {};
    const sm = reading.summary || {};
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${t.nav_title} — ${esc(form.name)} & ${esc(form.partner_name)}</title>
      <style>body{font-family:Georgia,serif;max-width:760px;margin:40px auto;padding:0 24px;color:#2a1a22;background:#fdf8f0;line-height:1.7}
      h1{color:#a8842c;font-size:32px}h2{color:#a8842c;border-bottom:1px solid #e0cfa0;padding-bottom:6px;margin-top:40px}
      h3{color:#8a5a6a;margin-bottom:6px}.card{border:1px solid #e6d6b8;border-radius:10px;padding:16px;margin:16px 0}
      .card img{width:140px;border-radius:8px;float:right;margin:0 0 10px 16px}.card{overflow:hidden}
      em{color:#666}</style></head><body>
      <h1>${t.nav_title}</h1>
      <p><b>${t.result_for}:</b> ${esc(form.name)} & ${esc(form.partner_name)}</p>
      <p><em>${esc(form.question)}</em></p>
      <p>${esc(reading.intro)}</p>
      <h2>${t.section1}</h2>${cardsHtml}
      <h2>${t.section2}</h2>
      <p><b>${t.love_feels}</b><br>${esc(lv.feels)}</p>
      <p><b>${t.love_blocks}</b><br>${esc(lv.blocks)}</p>
      <p><b>${t.love_chances}</b><br>${esc(lv.chances)}</p>
      <p><b>${t.love_understand}</b><br>${esc(lv.understand)}</p>
      <h2>${t.section3}</h2>${chakraHtml}
      <h2>${t.section4}</h2>
      <p><b>${t.sum_message}:</b> ${esc(sm.message)}</p>
      <p><b>${t.sum_core}:</b> ${esc(sm.core)}</p>
      <p><b>${t.sum_release}:</b> ${esc(sm.release)}</p>
      <p><b>${t.sum_invite}:</b> ${esc(sm.invite)}</p>
      <p><b>${t.sum_next}:</b> ${esc(sm.next_step)}</p>
      </body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Seelenpartner-Tarot-${form.name}-${form.partner_name}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="App min-h-screen bg-stars">
      <Toaster position="top-center" theme="dark" richColors />
      <Header lang={lang} setLang={setLang} t={t} onHome={reset} />

      {step === "landing" && <PortalLanding t={t} lang={lang} onStart={() => setStep("portal")} />}
      {step === "portal" && <PortalTransition lang={lang} onEnter={() => setStep("form")} />}
      {step === "form" && (
        <ReadingForm
          t={t}
          onSubmit={(f) => {
            setForm(f);
            setStep("draw");
          }}
        />
      )}
      {step === "draw" && <CardDraw t={t} lang={lang} onReveal={handleReveal} />}
      {step === "loading" && <Loading t={t} />}
      {step === "result" && reading && (
        <ResultView t={t} lang={lang} reading={reading} drawn={drawn} form={form} onNew={reset} onDownload={handleDownload} />
      )}

      <Footer lang={lang} />
    </div>
  );
}

export default App;
